import type { ChangeDonglesResponseDto, CreateDongleDto, DongleDto } from '@/castapi';
import { AppLogger } from '@/logger';
import type { ISearchParams } from '@/castapi/helpers';
import { getDonglesApi, getErrorMessage } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';
import type { ITableParamsState } from '@/store/modules/common/tableParamsState';
import TableParamsState from '@/store/modules/common/tableParamsState';

const logger = new AppLogger('adminInventory state');
const tableParams = new TableParamsState();

interface IAdminInventoryState extends ITableParamsState {
  inventory: DongleDto[];
  inventoryLoading: boolean;
  inventoryLoadError?: string;
  addInventoryLoading: boolean;
  addInventoryLoadError?: string;
  duplicatedDongleCodes: string[];
  createdDongleCodes: string[];
  deletedDongleNumbers: string[];
  donglesCanNotBeDeletedNumbers: string[];
  deleteInventoryError?: string;
}

const initialState = (): IAdminInventoryState => ({
  ...tableParams.state(),
  inventory: [],
  inventoryLoading: false,
  inventoryLoadError: undefined,
  addInventoryLoading: false,
  addInventoryLoadError: undefined,
  duplicatedDongleCodes: [],
  createdDongleCodes: [],
  deletedDongleNumbers: [],
  donglesCanNotBeDeletedNumbers: [],
  deleteInventoryError: undefined,
});

type ActionParams = IActionParams<IAdminInventoryState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    ...tableParams.mutations,

    INVENTORY_LOADING(state: IAdminInventoryState): void {
      state.inventoryLoadError = undefined;
      state.inventoryLoading = true;
      state.deleteInventoryError = undefined;
      state.deletedDongleNumbers = [];
      state.donglesCanNotBeDeletedNumbers = [];
    },

    INVENTORY_LOADED(state: IAdminInventoryState, payload: DongleDto[]): void {
      state.inventory = payload;
      state.inventoryLoading = false;
    },

    INVENTORY_LOAD_ERROR(state: IAdminInventoryState, error: Error): void {
      state.inventoryLoadError = getErrorMessage(error);
      state.inventoryLoading = false;
    },

    ADD_INVENTORY_LOADING(state: IAdminInventoryState): void {
      state.addInventoryLoadError = undefined;
      state.addInventoryLoading = true;
      state.duplicatedDongleCodes = [];
      state.createdDongleCodes = [];
    },

    ADD_INVENTORY(state: IAdminInventoryState, payload: ChangeDonglesResponseDto): void {
      state.duplicatedDongleCodes = payload.dongleNotChangedNumbers;
      state.createdDongleCodes = payload.dongleChangedNumbers;
      state.addInventoryLoading = false;
    },
    ADD_INVENTORY_LOAD_ERROR(state: IAdminInventoryState, error: Error): void {
      state.addInventoryLoadError = getErrorMessage(error);
      state.addInventoryLoading = false;
    },

    REMOVE_INVENTORY(state: IAdminInventoryState, payload: ChangeDonglesResponseDto): void {
      state.inventoryLoading = false;
      state.donglesCanNotBeDeletedNumbers = payload.dongleNotChangedNumbers;
      state.deletedDongleNumbers = payload.dongleChangedNumbers;
    },

    REMOVE_INVENTORY_ERROR(state: IAdminInventoryState, error: Error): void {
      state.inventoryLoading = false;
      state.deleteInventoryError = getErrorMessage(error);
    },

    RESET_STATE(state: IAdminInventoryState): void {
      Object.assign(state, initialState());
    },
  },
  actions: {
    ...tableParams.actions,

    async loadInventory({ commit, rootGetters }: ActionParams, searchParams: ISearchParams): Promise<void> {
      const { limit, offset, searchText, sortBy, sortDesc } = searchParams || {};
      try {
        // commit('RESET_STATE');
        commit('INVENTORY_LOADING');
        const response = await getDonglesApi(rootGetters['login/accessToken']).donglesControllerGetAllInventory(
          limit,
          offset,
          searchText,
          sortBy,
          sortDesc,
        );
        commit('INVENTORY_LOADED', response.data);
      } catch (error) {
        commit('INVENTORY_LOAD_ERROR', error);
        logger.captureStoreError('loadInventory', error, { limit, offset, searchText, sortBy, sortDesc });
      }
    },

    async addInventory({ commit, dispatch, rootGetters }: ActionParams, data: CreateDongleDto[]): Promise<void> {
      try {
        commit('ADD_INVENTORY_LOADING');
        const response = await getDonglesApi(rootGetters['login/accessToken']).donglesControllerLoadInventory(data);
        commit('ADD_INVENTORY', response.data);
        await dispatch('loadInventory');
      } catch (error) {
        commit('ADD_INVENTORY_LOAD_ERROR', error);
        logger.captureStoreError('addInventory', error, data);
      }
    },

    async removeInventory({ commit, rootGetters }: ActionParams, data: string[]): Promise<void> {
      try {
        commit('INVENTORY_LOADING');
        const response = await getDonglesApi(rootGetters['login/accessToken']).donglesControllerRemoveInventoryItem(
          data,
        );
        commit('REMOVE_INVENTORY', response.data);
      } catch (error) {
        commit('REMOVE_INVENTORY_ERROR', error);
        logger.captureStoreError('removeInventory', error, data);
      }
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
  },
  getters: {
    ...tableParams.getters,

    inventory: (state: IAdminInventoryState): DongleDto[] => state.inventory,
    inventoryLoading: (state: IAdminInventoryState): boolean => state.inventoryLoading,
    inventoryLoadError: (state: IAdminInventoryState): undefined | string => state.inventoryLoadError,
    addInventoryLoading: (state: IAdminInventoryState): boolean => state.addInventoryLoading,
    addInventoryLoadError: (state: IAdminInventoryState): undefined | string => state.addInventoryLoadError,
    // duplicatedDongleCodes: (state: IAdminInventoryState) => state.duplicatedDongleCodes,
    // createdDongleCodes: (state: IAdminInventoryState) => state.createdDongleCodes,
    // donglesCanNotBeDeletedNumbers: (state: IAdminInventoryState) => state.donglesCanNotBeDeletedNumbers,
    // deletedDongleNumbers: (state: IAdminInventoryState) => state.deletedDongleNumbers,
  },
};
